import { useState } from 'react'
import { api } from '@/lib/api'
import { Modal } from '@/shared/components/Modal'

const ROLES = ['owner', 'admin', 'member', 'reader'] as const
type Role = typeof ROLES[number]

interface Props {
  orgId: string
  orgName: string
  myRole: string
  onClose: () => void
  onInvited: () => void
}

export function InviteOrgMemberModal({ orgId, orgName, myRole, onClose, onInvited }: Props) {
  const [email, setEmail]   = useState('')
  const [role, setRole]     = useState<Role>('member')
  const [errors, setErrors] = useState<{ email?: string }>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [busy, setBusy]     = useState(false)

  const availableRoles = ROLES.filter((r) => myRole === 'owner' || r !== 'owner')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) { setErrors({ email: 'Email is required.' }); return }
    setBusy(true)
    setApiError(null)
    try {
      await api.post(`/api/orgs/${orgId}/members`, { email: email.trim(), role })
      onInvited()
    } catch (err) {
      setApiError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal track="invite_org_member" title="Invite member" kicker={`// ${orgName}`} onClose={onClose} maxWidth={420}>
      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
        <div>
          <label className="field-label">Email</label>
          <input
            className="input"
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors({}) }}
            autoFocus
            required
          />
          {errors.email && <div style={fieldError}>{errors.email}</div>}
        </div>
        <div>
          <label className="field-label">Role</label>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {availableRoles.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        {apiError && <div className="feedback-error">{apiError}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? <span className="spinner" /> : 'Invite'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

const fieldError: React.CSSProperties = {
  marginTop: 4,
  fontSize: '0.72rem',
  color: 'var(--red)',
}
